import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getPerformanceIndicators } from '../../../../api/performance-indicators';
import type { PerformanceIndicatorsData } from '@shared/types';

interface PerformanceIndicatorsState {
  data: PerformanceIndicatorsData | null;
  loading: boolean;
  error: string | null;
  selectedYear: number;
  availableYears: number[];
}

const POLICY_FIRST_YEAR = 2013;
const POLICY_LAST_YEAR = 2025;
const DEFAULT_YEAR = 2024;

// Generate year list from POLICY_LAST_YEAR down to POLICY_FIRST_YEAR
const generateYearList = (): number[] => {
  const years: number[] = [];
  for (let year = POLICY_LAST_YEAR; year >= POLICY_FIRST_YEAR; year--) {
    years.push(year);
  }
  return years;
};

const initialState: PerformanceIndicatorsState = {
  data: null,
  loading: false,
  error: null,
  selectedYear: DEFAULT_YEAR,
  availableYears: generateYearList(),
};

export const fetchPerformanceIndicators = createAsyncThunk(
  'performanceIndicators/fetch',
  async ({ companyId, year }: { companyId: string | number; year?: number }) => {
    const response = await getPerformanceIndicators(companyId, year);
    return response;
  },
);

const PerformanceIndicatorsSlice = createSlice({
  name: 'companyPerformanceIndicators',
  initialState,
  reducers: {
    resetPerformanceIndicators: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPerformanceIndicators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformanceIndicators.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data ?? null;
        state.error = null;
      })
      .addCase(fetchPerformanceIndicators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to load performance indicators';
      });
  },
});

export const { resetPerformanceIndicators, setSelectedYear } = PerformanceIndicatorsSlice.actions;

export default PerformanceIndicatorsSlice.reducer;
